import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { AppSidebar } from '@/components/app-sidebar';
import { DemoBanner } from '@/components/demo-banner';
import { GlobalDrawers } from '@/components/global-drawers';
import { Toaster } from '@/components/toast';
import { TopBar } from '@/components/top-bar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { auth } from '@/lib/auth';
import { useClients, useDemoPersona, useMe } from '@/lib/clients';
import { useHotkeys } from '@/lib/hotkeys';
import { setOrgId, useOrgId } from '@/lib/org';
import { setLanguage } from '@/lib/i18n';
import { notifyOrgLocale, setOrgLocale } from '@/lib/org-locale';

/** Pages anyone can open: the home page, sign in, sign up, the demo and the sign-in callback. */
export const isPublicPath = (pathname: string): boolean =>
  pathname === '/' ||
  pathname === '/login' ||
  pathname === '/signup' ||
  pathname === '/demo' ||
  pathname.startsWith('/auth/');

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: async ({ location }) => {
    if (!auth.enabled || isPublicPath(location.pathname)) return;
    if (!(await auth.getUser())) {
      // an expired demo session goes back into the demo, not to Log in
      const demo = await auth.wasDemo();
      if (demo) throw redirect({ to: '/demo', search: { as: demo } });
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

function AppShell() {
  // Public pages and the sign-in callback must not mount the shell: its queries would run
  // before a token exists. Set up your company (W5) is signed in but has no company to show yet.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const publicPage = isPublicPath(pathname);
  if (publicPage || pathname === '/onboarding') return <Outlet />;
  return <Shell />;
}

function Shell() {
  const me = useMe(auth.enabled);
  // the open client's currency and number grouping (INR in lakhs and crores for India)
  const orgId = useOrgId();
  const clientsQuery = useClients(auth.enabled);
  const clients = clientsQuery.data;
  const client = clients?.find((c) => c.id === orgId) ?? null;
  const navigate = useNavigate();
  // a signed-up user (ADR-0021) has no company yet: set one up first; staff see an empty practice
  // only on a settled answer: a stale cached empty list is refetching, not "no company"
  const needsCompany = Boolean(
    me.data && !me.data.isStaff && clients?.length === 0 && !clientsQuery.isFetching,
  );
  useEffect(() => {
    if (needsCompany) void navigate({ to: '/onboarding', replace: true });
  }, [needsCompany, navigate]);
  // The public demo (ADR-0020) opens on its first client (the Ganga Traders copy), also when this
  // browser last had a client the demo cannot see.
  const demo = useDemoPersona();
  useEffect(() => {
    if (demo && clients?.length && !clients.some((c) => c.id === orgId)) setOrgId(clients[0]!.id);
  }, [demo, clients, orgId]);
  setOrgLocale(client);
  const locale = `${client?.taxPack ?? 'in'}-${client?.baseCurrency ?? 'INR'}-${client?.stateCode ?? ''}-${client?.arabicEnabled ? 'ar' : ''}`;
  // the user's own language (document 15, D2), from their profile; this browser remembers it
  const language = me.data?.language;
  useEffect(() => {
    if (language) void setLanguage(language);
  }, [language]);
  useEffect(() => notifyOrgLocale(), [locale]);
  // Global QBO shortcuts (document 05); form-level ones live in the forms, search (Ctrl+Alt+F, /)
  // in GlobalSearch.
  useHotkeys({
    'ctrl+alt+a': () => void navigate({ to: '/accounting/chart-of-accounts' }),
    'ctrl+alt+c': () => void navigate({ to: '/sales/customers' }),
    'ctrl+alt+i': () => void navigate({ to: '/sales/invoices/new' }),
    'ctrl+alt+r': () => void navigate({ to: '/sales/receive-payment/new' }),
    'ctrl+alt+e': () => void navigate({ to: '/sales/quotes/new' }),
    'ctrl+alt+v': () => void navigate({ to: '/expenses/vendors' }),
    'ctrl+alt+/': () => void navigate({ to: '/help/shortcuts' }),
  });
  return (
    <SidebarProvider>
      <AppSidebar isStaff={me.data?.isStaff ?? true} />
      <SidebarInset className="min-w-0">
        {demo ? <DemoBanner persona={demo} /> : null}
        <TopBar />
        <main key={locale} className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </SidebarInset>
      <Toaster />
      <GlobalDrawers />
    </SidebarProvider>
  );
}
